import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import api from '../../../../../services/api';
import { fetchRFPById, fetchRFPOverview, fetchMyTeam, fetchMySections, fetchAssignedUsers } from '../../../services/rfpCoreService';
import { normalizeMetadata } from '../utils/helperUtils';

/**
 * Hook to manage RFP data fetching, normalization, and team members
 * Uses centralized api service — token, headers, and baseURL handled automatically.
 */
export const useRFPData = (rfpId, stateData, locationState, isManager, setAssignments) => {
    const [editableData, setEditableData] = useState(() => {
        if (!stateData) return null;
        return normalizeMetadata(stateData, locationState);
    });
    const [teamMembers, setTeamMembers] = useState([]);
    const [assignedUsers, setAssignedUsers] = useState([]);
    const [isInitialLoading, setIsInitialLoading] = useState(!stateData);
    const [loading, setLoading] = useState(false);
    const [effectiveIsManager, setEffectiveIsManager] = useState(isManager);
    const [accessError, setAccessError] = useState(null);
    const teamMembersRef = useRef(teamMembers);

    useEffect(() => {
        setEffectiveIsManager(isManager);
    }, [isManager]);

    useEffect(() => {
        teamMembersRef.current = teamMembers;
    }, [teamMembers]);

    const token = localStorage.getItem('access_token');

    const fetchTeamMembers = useCallback(async () => {
        try {
            const response = await fetchMyTeam();
            const data = response.data;
            setTeamMembers(Array.isArray(data) ? data : (data.team || []));
        } catch (error) {
            console.error('Error fetching team members:', error);
        }
    }, []);

    const fetchRFPData = useCallback(async (isSilent = false) => {
        if (!rfpId) return;

        // Skip normal RFP GET if we already know from state it is pending decision (status 12)
        const stateCode = locationState?.globalStatusCode || locationState?.analysisData?.global_status?.code;
        if (Number(stateCode) === 12) {
            setIsInitialLoading(false);
            return;
        }

        if (!isSilent) {
            setIsInitialLoading(true);
        }
        setAccessError(null);

        try {
            let response;
            let currentLeadAccess = isManager;

            if (isManager) {
                console.log(`📡 [API] Triggering RFP GET for Team Lead: ${rfpId}`);
                try {
                    response = await fetchRFPById(rfpId);
                    setEffectiveIsManager(true);
                } catch (leadErr) {
                    if (leadErr.response?.status === 403) {
                        console.warn(`⚠️ [API] User does not have Team Lead access for RFP ${rfpId} (403 Forbidden). Falling back to employee assigned sections...`);
                        currentLeadAccess = false;
                        setEffectiveIsManager(false);
                        try {
                            response = await fetchMySections(rfpId);
                        } catch (memberErr) {
                            console.error('Error fetching employee sections after lead 403:', memberErr);
                            throw memberErr;
                        }
                    } else {
                        throw leadErr;
                    }
                }
            } else {
                console.log(`📡 [API] Triggering RFP GET for Team Member: ${rfpId}`);
                response = await fetchMySections(rfpId);
                setEffectiveIsManager(false);
            }

            if (response?.data) {
                const data = response.data;
                if (data?.global_status?.code === 12) {
                    setEditableData(normalizeMetadata(data));
                    if (!isSilent) setIsInitialLoading(false);
                    return;
                }

                const normalized = normalizeMetadata(data);
                setEditableData(normalized);

                // Populate assignments state for team member flow to enable sidebar status badges and tooltips
                if (!currentLeadAccess && typeof setAssignments === 'function' && normalized.sections) {
                    const teamMemberAssigns = {};
                    normalized.sections.forEach(s => {
                        const key = s.label || s.key || s.section_title;
                        if (key) {
                            teamMemberAssigns[key] = ['You'];
                        }
                    });
                    setAssignments(teamMemberAssigns);
                }
            }
        } catch (err) {
            console.error('Error fetching RFP data:', err);
            const status = err.response?.status;
            const message = err.response?.data?.message || err.response?.data?.detail || err.message;
            if (status === 403) {
                setAccessError(`Access Denied (403): You do not have permission to view or edit this proposal (${rfpId}).`);
            } else {
                setAccessError(message || 'Failed to load RFP data.');
            }
        } finally {
            if (!isSilent) {
                setIsInitialLoading(false);
            }
        }
    }, [rfpId, isManager, setAssignments, locationState]);

    const fetchOverview = useCallback(async (assignmentsCallback) => {
        if (!rfpId || !effectiveIsManager) return null;
        try {
            const response = await fetchRFPOverview(rfpId);
            const data = response.data;
            
            const rawAssignments = data?.assignments || data?.section_assignments || data?.SECTION_ASSIGNMENTS || [];
            const normalizedMap = {};

            // Transform array of assignments into a robust lookup map
            if (Array.isArray(rawAssignments)) {
                rawAssignments.forEach(asm => {
                    let usersList = [];
                    if (Array.isArray(asm.assigned_users)) {
                        usersList = asm.assigned_users;
                    } else if (asm.assigned_user) {
                        usersList = [asm.assigned_user];
                    }

                    if (usersList.length === 0) return; // Skip if no users are assigned

                    const normalizedMembers = usersList.map(usr => {
                        let user = usr || {};
                        let userName = 'Assigned Member';

                        if (typeof usr === 'string') {
                            userName = usr;
                            user = { name: userName };
                        } else {
                            userName = user.name || user.first_name || asm.assigned_to || 'Assigned Member';
                        }
                        
                        const userId = user._id || user.id || user.user_id || asm.assigned_to_user_id;

                        // Enrichment: If name is generic but we have an ID, look it up in the team list
                        if ((userName === 'Assigned' || userName === 'Assigned Member') && userId && teamMembersRef.current.length > 0) {
                            const match = teamMembersRef.current.find(m => (m._id || m.id) === userId);
                            if (match) {
                                userName = match.name || `${match.first_name} ${match.last_name}`;
                            }
                        }
                        
                        return { 
                            ...user,
                            id: userId,
                            name: userName,
                            deadline: user.deadline || asm.deadline // Capture deadline from user or assignment level
                        };
                    });
                    
                    // Index by all possible keys to ensure UI matching works
                    if (asm.title) normalizedMap[asm.title] = normalizedMembers;
                    if (asm.section_key) normalizedMap[asm.section_key] = normalizedMembers;
                    if (asm.section_id) normalizedMap[asm.section_id] = normalizedMembers;

                    // Store extension metadata with prefixes for easy lookup in Sidebar
                    const extMeta = { 
                        status: asm.extension_status, 
                        request: asm.extension_request 
                    };
                    if (asm.title) normalizedMap[`ext_${asm.title}`] = extMeta;
                    if (asm.section_key) normalizedMap[`ext_${asm.section_key}`] = extMeta;
                    if (asm.section_id) normalizedMap[`ext_${asm.section_id}`] = extMeta;
                    
                    // Case-insensitive fallbacks
                    if (asm.title) normalizedMap[asm.title.toLowerCase()] = normalizedMembers;
                    if (asm.section_key) normalizedMap[asm.section_key.toLowerCase()] = normalizedMembers;
                    if (asm.title) normalizedMap[`ext_${asm.title.toLowerCase()}`] = extMeta;
                    if (asm.section_key) normalizedMap[`ext_${asm.section_key.toLowerCase()}`] = extMeta;
                });
            }
 else if (typeof rawAssignments === 'object' && rawAssignments !== null) {
                // Handle Map format: { "Section Title": ["Member Name", ...] }
                Object.entries(rawAssignments).forEach(([title, members]) => {
                    if (!Array.isArray(members)) return;
                    
                    const normalizedMembers = members.map(m => {
                        let userName = typeof m === 'string' ? m : (m.name || m.first_name || 'Assigned Member');
                        let userId = typeof m === 'object' ? (m.id || m._id || m.user_id) : null;
                        
                        // If we have a name but no ID, try to find the ID in the team list
                        if (!userId && userName && teamMembersRef.current.length > 0) {
                            const match = teamMembersRef.current.find(tm => 
                                tm.name === userName || 
                                `${tm.first_name} ${tm.last_name}` === userName
                            );
                            if (match) userId = match._id || match.id;
                        }

                        return { id: userId, name: userName };
                    });

                    normalizedMap[title] = normalizedMembers;
                    normalizedMap[title.toLowerCase()] = normalizedMembers;
                });
            }

            // Prioritize passed callback, fallback to hook's setAssignments
            if (typeof assignmentsCallback === 'function') {
                assignmentsCallback(normalizedMap);
            } else if (typeof setAssignments === 'function') {
                setAssignments(normalizedMap);
            }
            return data;
        } catch (error) {
            if (error.response?.status !== 403) {
                console.error('Error fetching RFP overview:', error);
            }
            return null;
        }
    }, [rfpId, effectiveIsManager, setAssignments]);

    const fetchAssignedUsersData = useCallback(async () => {
        if (!rfpId || !effectiveIsManager) return null;
        try {
            console.log(`📡 [API] Triggering Assigned Users GET for Team Lead: ${rfpId}`);
            const response = await fetchAssignedUsers(rfpId);
            const data = response.data;
            setAssignedUsers(Array.isArray(data) ? data : (data.users || []));
            return data;
        } catch (error) {
            if (error.response?.status !== 403) {
                console.error('Error fetching assigned users:', error);
            }
            return null;
        }
    }, [rfpId, effectiveIsManager]);

    const safeData = editableData || {};

    // Headers, Lists, Links, Blockquotes, code, and emphasis
    const markdownRegex = /^(?:[#*+-]|>\s|\d+\.\s|`|\[|!\[|_|~)/;

    const sidebarSections = useMemo(() => {
        return (safeData.sections && Array.isArray(safeData.sections))
            ? safeData.sections.filter(s => (s.label || s.key) !== 'Key Information')
            : [];
    }, [safeData.sections]);

    useEffect(() => {
        if (rfpId) {
            const stateCode = locationState?.globalStatusCode || locationState?.analysisData?.global_status?.code;
            if (Number(stateCode) === 12) {
                setIsInitialLoading(false);
                return;
            }

            fetchRFPData();
        }
    }, [rfpId, fetchRFPData, locationState]);

    useEffect(() => {
        if (rfpId && effectiveIsManager && editableData) {
            fetchTeamMembers();
            fetchOverview(setAssignments);
            fetchAssignedUsersData();
        }
    }, [rfpId, effectiveIsManager, editableData, fetchTeamMembers, fetchOverview, setAssignments, fetchAssignedUsersData]);

    return useMemo(() => ({
        editableData,
        setEditableData,
        teamMembers,
        assignedUsers,
        fetchTeamMembers,
        isInitialLoading,
        loading,
        setLoading,
        sidebarSections,
        fetchRFPData,
        fetchOverview,
        fetchAssignedUsersData,
        effectiveIsManager,
        accessError
    }), [editableData, teamMembers, assignedUsers, fetchTeamMembers, isInitialLoading, loading, sidebarSections, fetchRFPData, fetchOverview, fetchAssignedUsersData, effectiveIsManager, accessError]);
};
